// In front of every page when the portal is on the API (LH-B1): checks the session, shows sign-in until the API says
// "ok", then reads the lenders, History, Alerts, events, Settings (and the users, for admins) and opens the portal.
// On the fixtures (no API) it lets everything through at once.
import { useEffect, useState, type ReactNode } from 'react'
import { Modal } from '@/kit/ui'
import { apiMode, hub } from '@/lib/hubApi'
import { loadFromHub, useHubStore } from '@/store/hubStore'
import { useSessionStore } from '@/store/sessionStore'
import { useSettingsStore } from '@/store/settingsStore'
import { SignInScreen } from './SignInScreen'

function Splash({ text, children }: { text: string; children?: ReactNode }) {
  return <div className="flex min-h-screen flex-col items-center justify-center gap-2 text-sm text-muted" data-testid="hub-splash">{text}{children}</div>
}

function ApiGate({ children }: { children: ReactNode }) {
  const { status, view, check, recoveryCodes, dismissCodes } = useSessionStore()
  const [loaded, setLoaded] = useState<'no' | 'yes' | 'error'>('no')
  const [error, setError] = useState('')
  const ok = status === 'in' && view?.stage === 'ok'

  useEffect(() => { void check() }, [check])
  useEffect(() => {
    if (!ok || !view) { setLoaded('no'); return }
    const me = { id: view.user.id, name: view.user.name, email: view.user.email, role: view.user.role, twoStep: view.user.two_step, lastSignInAt: null }
    // an MIS uploader (HM2) reads only the MIS pages: nothing of the lenders is loaded for them
    const lenders = view.permissions.includes('lenders.view') ? loadFromHub({ users: view.permissions.includes('users.manage'), me }) : Promise.resolve(useHubStore.setState({ users: [me] }))
    Promise.all([lenders, hub.settings().then((settings) => useSettingsStore.setState({ settings }))])
      .then(() => setLoaded('yes'))
      .catch((err: unknown) => { setError(err instanceof Error ? err.message : String(err)); setLoaded('error') })
  }, [ok, view?.user.id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'checking') return <Splash text="Checking your session…" />
  if (!ok) return <SignInScreen />
  if (loaded === 'error') return <Splash text={`The Hub did not answer: ${error}`}><button type="button" className="btn btn-secondary btn-sm" onClick={() => location.reload()}>Try again</button></Splash>
  if (loaded === 'no') return <Splash text="Loading the Hub…" />
  return (
    <>
      {children}
      <Modal isOpen={!!recoveryCodes} onClose={dismissCodes} title="Your recovery codes" footer={<div className="flex justify-end"><button type="button" className="btn btn-primary" onClick={dismissCodes}>I have kept them</button></div>}>
        <p className="mb-2 text-xs">Each code signs you in once if you lose your phone. Keep them somewhere safe: they are shown only now.</p>
        <ul className="mono grid grid-cols-2 gap-1 text-xs">{recoveryCodes?.map((c) => <li key={c}>{c}</li>)}</ul>
      </Modal>
    </>
  )
}

export function SessionGate({ children }: { children: ReactNode }) {
  return apiMode() ? <ApiGate>{children}</ApiGate> : <>{children}</>
}
